import { useRef, useState } from "react";

/**
 * useConfirmDialog
 *
 * Substitui os 7 useState separados de confirmação por um único estado.
 *
 * Uso:
 *   const { confirmState, openConfirm, closeConfirm } = useConfirmDialog();
 *
 *   openConfirm({
 *     title, message, confirmLabel, cancelLabel,
 *     confirmIcon, cancelIcon, icon, confirmVariant,
 *     onConfirm,
 *   });
 *
 * Renderização:
 *   <ConfirmDialog
 *     open={confirmState.open}
 *     {...confirmState}
 *     onClose={closeConfirm}
 *     onDismiss={closeConfirm}
 *     onConfirm={async () => { closeConfirm(); await confirmState.onConfirm?.(); }}
 *   />
 */
export function useConfirmDialog() {
  const [confirmState, setConfirmState] = useState({ open: false });
  const actionRef = useRef(null);

  function openConfirm({
    title = "Confirmação",
    message = "",
    confirmLabel = "Confirmar",
    cancelLabel = "Cancelar",
    confirmIcon = null,
    cancelIcon = null,
    icon = null,
    confirmVariant = "danger",
    onConfirm,
  } = {}) {
    actionRef.current = onConfirm ?? null;
    setConfirmState({
      open: true,
      title,
      message,
      confirmLabel,
      cancelLabel,
      confirmIcon,
      cancelIcon,
      icon,
      confirmVariant,
    });
  }

  function closeConfirm() {
    setConfirmState({ open: false });
    actionRef.current = null;
  }

  async function runConfirm() {
    const action = actionRef.current;  // captura antes de closeConfirm() zerar a ref
    closeConfirm();
    try {
      if (action) await action();
    } catch (err) {
      console.error("[useConfirmDialog] onConfirm error:", err);
    }
  }

  return { confirmState, openConfirm, closeConfirm, runConfirm };
}
